import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  CardAssignmentDTOType,
  useGetTravellerCardHistory,
  type CardAssignmentDTO,
} from '@/api/trafikportalen';
import { Spinner } from '@/components/core/Spinner';
import { cn } from '@/helpers/classnames';
import { formatCardSerial } from '@/helpers/cardSerial';
import { formatDate, formatDateTime } from '@/helpers/formatting';
import { toast } from '@/hooks/useToast';
import {
  IconChevronRight,
  IconGraduationCap,
  IconSmartcard,
  IconWallet,
} from '@/icons';
import Button from '../core/Button';

interface TravellerCardHistoryProps {
  travellerId: string;
}

const CARD_TYPE_ICON = {
  [CardAssignmentDTOType.travelcard]: IconSmartcard,
  [CardAssignmentDTOType.walletcard]: IconWallet,
  [CardAssignmentDTOType.schoolcard]: IconGraduationCap,
} as const;

const cardTypeLabel = (
  type: CardAssignmentDTO['type'],
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
) => {
  switch (type) {
    case CardAssignmentDTOType.travelcard:
      return formatMessage({ defaultMessage: 'Resekort' });
    case CardAssignmentDTOType.walletcard:
      return formatMessage({ defaultMessage: 'Tjänsteresa på kort' });
    case CardAssignmentDTOType.schoolcard:
      return formatMessage({ defaultMessage: 'Skolkort' });
  }
};

const AssignmentDate = ({ value }: { value: string }) => (
  <time dateTime={value} title={formatDateTime(value)}>
    {formatDate(value)}
  </time>
);

interface CardAssignmentRowProps {
  assignment: CardAssignmentDTO;
}

const CardAssignmentRow = ({ assignment }: CardAssignmentRowProps) => {
  const { formatMessage } = useIntl();
  const Icon = CARD_TYPE_ICON[assignment.type];

  return (
    <li className="flex flex-col gap-1 border-b border-b-background-200 py-2 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono">
          {formatCardSerial(assignment.card_serial)}
        </span>
        <span className="inline-flex h-min items-center gap-1 whitespace-nowrap rounded bg-background-400 px-1.5 py-0.5 font-medium text-text-800">
          <Icon className="size-4 shrink-0" aria-hidden="true" />
          {cardTypeLabel(assignment.type, formatMessage)}
        </span>
        {!assignment.released_at && (
          <span className="inline-flex h-min items-center rounded-full bg-green-500 px-2 py-0.5 text-sm text-contrast-green">
            <FormattedMessage defaultMessage="Nuvarande" />
          </span>
        )}
      </div>
      <p className="text-text-700">
        <FormattedMessage
          defaultMessage="Tilldelad {assigned}."
          values={{
            assigned: <AssignmentDate value={assignment.assigned_at} />,
          }}
        />
        {assignment.released_at && (
          <>
            {' '}
            <FormattedMessage
              defaultMessage="Frisläppt {released}."
              values={{
                released: <AssignmentDate value={assignment.released_at} />,
              }}
            />
          </>
        )}
      </p>
    </li>
  );
};

interface TravellerCardHistoryPanelProps {
  assignments?: CardAssignmentDTO[];
  isLoading: boolean;
  isError: boolean;
  panelId: string;
  labelledBy: string;
  descriptionId: string;
}

const TravellerCardHistoryPanel = ({
  assignments,
  isLoading,
  isError,
  panelId,
  labelledBy,
  descriptionId,
}: TravellerCardHistoryPanelProps) => {
  return (
    <div
      id={panelId}
      role="region"
      aria-labelledby={labelledBy}
      aria-busy={isLoading}
      className="mt-2 flex flex-col gap-2"
    >
      <p id={descriptionId} className="text-text-700">
        <FormattedMessage defaultMessage="Visar endast systemägda resekort, även kort som fortfarande är tilldelade." />
      </p>
      {isLoading ? (
        <Spinner className="size-8" />
      ) : isError ? (
        <p className="text-text-700">
          <FormattedMessage defaultMessage="Kunde inte hämta korthistorik" />
        </p>
      ) : !assignments?.length ? (
        <p className="italic text-text-700">
          <FormattedMessage defaultMessage="Det finns inga systemägda resekort i historiken för den här resenären." />
        </p>
      ) : (
        <ul className="flex flex-col">
          {assignments.map((assignment) => (
            <CardAssignmentRow
              key={`${assignment.bobcat_mtb_bearer_id}-${assignment.assigned_at}`}
              assignment={assignment}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export const TravellerCardHistory = ({
  travellerId,
}: TravellerCardHistoryProps) => {
  const { formatMessage } = useIntl();
  const [expanded, setExpanded] = useState(false);
  const buttonId = useId();
  const panelId = useId();
  const descriptionId = useId();
  const { data, isLoading, isError } = useGetTravellerCardHistory(travellerId);

  const previousCardCount = useMemo(() => {
    if (!data || isError) return undefined;
    return data.filter((assignment) => assignment.released_at).length;
  }, [data, isError]);

  useEffect(() => {
    if (expanded && isError) {
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Kunde inte hämta korthistorik',
          }),
        },
        { autoClose: 3000 },
      );
    }
  }, [expanded, formatMessage, isError]);

  const handleToggle = useCallback(() => {
    setExpanded((current) => !current);
  }, []);

  return (
    <>
      <Button
        id={buttonId}
        variant="ghost"
        className="w-full justify-start"
        aria-expanded={expanded}
        aria-controls={expanded ? panelId : undefined}
        aria-describedby={expanded ? descriptionId : undefined}
        onClick={handleToggle}
      >
        <span
          className={cn(
            'shrink-0 transition-transform',
            expanded && 'rotate-90',
          )}
          aria-hidden="true"
        >
          <IconChevronRight className="size-5" />
        </span>
        <FormattedMessage defaultMessage="Korthistorik" />
        {previousCardCount !== undefined && (
          <span className="ml-auto font-normal text-text-700">
            <FormattedMessage
              defaultMessage="{count, plural, =0 {Inga tidigare kort} one {# tidigare kort} other {# tidigare kort}}"
              values={{ count: previousCardCount }}
            />
          </span>
        )}
      </Button>
      {expanded && (
        <TravellerCardHistoryPanel
          assignments={data}
          isLoading={isLoading}
          isError={isError}
          panelId={panelId}
          labelledBy={buttonId}
          descriptionId={descriptionId}
        />
      )}
    </>
  );
};
